<template>
  <div class="main">
    <Transition name="error-toast-fade">
      <div class="error-toast" v-if="errorToastVisible">Bitte überprüfe deine Eingaben.</div>
    </Transition>
    <div class="profile-wrapper" :class="{details_expanded: details_expanded}">
      <div class="header-controls" v-if="!details_expanded">
        <GroupSelector 
          v-model="selectedPool" 
          :pools="pools" 
          @change="poolChanged()"
          style="flex: 1; margin: 0;"
        />
        <router-link :to="'/settings'" class="settings-link"><img src="@/assets/images/settings.svg"></router-link>
      </div>
      <LoadingSpinner v-if="profileLoading" message="Profil lädt..."/>
      
      <!-- Admin Tab Selector -->
      <div class="tab-selector" v-if="isAdmin && !details_expanded">
        <button :class="{ active: activeTab === 'profile' }" @click="activeTab = 'profile'">Profil</button>
        <button :class="{ active: activeTab === 'admin' }" @click="activeTab = 'admin'; loadAdminData()">Admin</button>
      </div>

      <div v-if="activeTab === 'profile'" class="profile-content-container" style="width: 100%; display: flex; flex-direction: column; align-items: center;">
        <div class="joined-banner" v-if="$route.query.joined && !details_expanded && noProfileYet">
          ✓ Du bist der Gruppe erfolgreich beigetreten! Erstelle jetzt dein Profil, damit andere Mitglieder dich sehen können.
        </div>
        <div v-if="noProfileYet && !creatingProfile" class="add_profile_wrapper">
          <h3>Du hast für diese Gruppe noch kein Profil eingerichtet!</h3>
          <button class="add_profile" @click="creatingProfile = true; getProfilePrefill(); details_expanded = true;">Profil erstellen</button>
        </div>
        <div v-if="!pools_available" class="add_profile_wrapper">
          <h3>Du bist noch kein Mitglied in einer Gruppe!</h3>
          <p>Tritt auf der Startseite zuerst einer Gruppe bei.</p>
        </div>
        <h1 v-if="!details_expanded && !noProfileYet && pools_available && !profileLoading">Dein Profil</h1>
        <div class="match-selection-container" :class="{details_expanded: details_expanded}" v-if="(!noProfileYet || creatingProfile) && pools_available && !profileLoading">
          <div class="match-info" v-if="!details_expanded && !creatingProfile">
            <h2 id="profile">{{profile.name}}</h2>
            <div class="match-stat">
              <img src="@/assets/images/cake.svg">
              <p><b>{{profile.age}}</b> Jahre</p>
            </div>
            <div class="match-stat">
              <img src="@/assets/images/person-height.svg">
              <p><b>{{profile.height}}</b> cm</p>
            </div>
            <div class="match-stat" v-if="profile.gender">
              <img src="@/assets/images/account.svg">
              <p>{{ gender_options.find(g => g.value === profile.gender)?.label }}</p>
            </div>
            <div class="match-stat">
              <img src="@/assets/images/search.svg">
              <p>{{profile.looking_for}}</p>
            </div>
            <div class="match-stat" v-if="profile.availability && profile.availability.length > 0">
              <img src="@/assets/images/calendar.svg">
              <div class="match-dates"><p v-for="av in profile.availability" :key="av.name">{{av.name}}</p></div>
            </div>
            <div class="match-stat match-note" v-if="profile.short_bio">
              <img src="@/assets/images/notes.svg">
              <p style="word-break: break-all; white-space: normal;">{{profile.short_bio}}</p>
            </div>
            <h3 v-if="profile.long_bio">Details</h3>
            <div class="details-container fade-gradient" id="details">
              <p style="word-break: break-all; white-space: normal;">{{profile.long_bio}}</p>
            </div>
            <button class="edit-btn" @click="details_expanded = !details_expanded">Bearbeiten</button>
          </div>
          <div class="match-info-wrapper" v-if="details_expanded || creatingProfile">
            <div class="match-info edit" :class="{ disabled_loading: submitting }"> <!-- ***** edit view here *****-->
            <LoadingSpinner class="submitting" v-if="submitting" message="Profil wird gespeichert..."/>
            <input type="text" id="name" v-model="profile.name" placeholder="Dein Name">
            <p class="profile-error" v-if="errors.find(e => e.field === 'name')">{{ errors.find(e => e.field === 'name').e }}</p>
            <div class="match-stat">
              <img src="@/assets/images/cake.svg">
              <input type="date" id="birthday" v-model="profile.birthday">
            </div>
            <p class="profile-error" v-if="errors.find(e => e.field === 'birthday')">{{ errors.find(e => e.field === 'birthday').e }}</p>
            <div class="match-stat">
              <img src="@/assets/images/person-height.svg">
              <input type="number" id="height" v-model="profile.height"><p>cm</p>
            </div>
            <p class="profile-error" v-if="errors.find(e => e.field === 'height')">{{ errors.find(e => e.field === 'height').e }}</p>
            <div class="match-stat">
              <img src="@/assets/images/account.svg">
              <select id="gender" v-model="profile.gender">
                <option :value="null" disabled>Dein Geschlecht</option>
                <option v-for="g in gender_options" :key="g.value" :value="g.value">{{ g.label }}</option>
              </select>
            </div>
            <div class="match-stat">
              <img src="@/assets/images/search.svg">
              <input type="text" id="looking_for" v-model="profile.looking_for" placeholder="Wonach suchst du?">
            </div>
            <p class="profile-error" v-if="errors.find(e => e.field === 'looking_for')">{{ errors.find(e => e.field === 'looking_for').e }}</p>
            <div class="match-stat">
              <img src="@/assets/images/calendar.svg">
              <multiselect id="tagging" v-model="profile.availability" tag-placeholder="" select-label="" placeholder="Verfügbarkeit" label="name"
                            track-by="code" :options="availability_options" :multiple="true" :taggable="true" :hide-selected="true" :close-on-select="false" @tag="addTag"/>
            </div>
            <div class="match-stat match-note">
              <img src="@/assets/images/notes.svg">
              <input type="text" v-model="profile.short_bio" placeholder="Kurzbeschreibung">
            </div>
            <h3>Details</h3>
            <div class="details-container edit" id="details">
              <textarea v-model="profile.long_bio" rows="4" placeholder="Detailbeschreibung"></textarea>
            </div>
            <h3>Fotos ({{profile.images ? profile.images.filter(i => i).length  + "/" + 3 : "0/3"}})</h3>
            <div class="photos-container" id="photos">
              <div class="single-photo" v-for="(image, i) in profile.images" :key="i">
                <button v-if="image" class="delete" @click="removeImage(image.image_id)"><img src="@/assets/images/delete.svg"></button>
                <img v-if="image" :src="image.image">
              </div>
              <div class="single-photo" v-if="profile.images && profile.images.length < 3 && !uploadingImage" :class="{full_width: profile.images.length === 0}">
                <div>
                  <button @click="openUploadDialog">Foto hinzufügen</button>
                </div>
              </div>
              <div class="single-photo" v-if="uploadingImage">
                <div class="photo-loading">
                  <LoadingSpinner message="Bild wird hochgeladen..." />
                </div>
              </div>
            </div>
          </div>
          </div>
          <div class="submit-buttons" v-if="details_expanded || creatingProfile">
              <button class="edit-btn logout" @click="creatingProfile = false; details_expanded = false;">Abbrechen</button>
              <button class="edit-btn" @click="submitProfile()">Speichern</button>
          </div>
        </div>
      </div>

      <!-- Admin Panel Section -->
      <div v-if="activeTab === 'admin' && isAdmin" class="admin-panel" style="width: 100%; display: flex; flex-direction: column; align-items: stretch; gap: 24px;">
        <h2>Admin-Panel</h2>

        <!-- Section: Homepage Hits -->
        <div class="admin-section">
          <h3>Website-Aufrufe</h3>
          <div class="stat-tile">
            <span class="stat-number">{{ homepageHitsCount !== null ? homepageHitsCount : '–' }}</span>
            <span class="stat-label">Aufrufe der Startseite</span>
          </div>
        </div>

        <!-- Section: Latest Registered Users -->
        <div class="admin-section">
          <h3>Neueste registrierte Benutzer</h3>
          <div class="admin-list users-list">
            <div v-for="u in latestUsers" :key="u.id" class="admin-item user-item">
              <div class="user-details">
                <span class="user-email">{{ u.email }}</span>
                <span class="user-dates">
                  Registriert: {{ formatDate(u.registered) }} 
                  <span v-if="u.last_login">| Letzter Login: {{ formatDate(u.last_login) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Section: Groups (Pools) -->
        <div class="admin-section">
          <h3>Alle Gruppen (Pools)</h3>
          <p class="section-desc">Klicke auf eine Gruppe, um die Mitgliederdetails anzuzeigen.</p>
          <div class="admin-list pools-list">
            <div 
              v-for="pool in adminPools" 
              :key="pool.id" 
              class="admin-item pool-item clickable"
              :class="{ selected: selectedAdminPool && selectedAdminPool.id === pool.id }"
              @click="selectAdminPool(pool)"
            >
              <div class="pool-header">
                <h4>{{ pool.name }} <span class="pool-code">({{ pool.code }})</span></h4>
                <span class="status-badge" :class="{ archived: pool.archived }">
                  {{ pool.archived ? 'Archiviert' : 'Aktiv' }}
                </span>
              </div>
              <p v-if="pool.description" class="pool-desc">{{ pool.description }}</p>
              <small class="pool-date">Erstellt am: {{ formatDate(pool.created_at) }}</small>
            </div>
          </div>
        </div>

        <!-- Section: Feedback -->
        <div class="admin-section">
          <h3>Feedback</h3>
          <p class="section-desc">Tippe auf einen Eintrag, um den vollständigen Text zu lesen.</p>
          <div class="admin-list feedback-list">
            <div
              v-for="fb in adminFeedback"
              :key="fb.id"
              class="admin-item feedback-item clickable"
              @click="selectFeedback(fb)"
            >
              <div class="feedback-meta">
                <span class="feedback-email">{{ fb.user_email || 'Anonym' }}</span>
                <span class="feedback-date">{{ formatDate(fb.created_at) }}</span>
              </div>
              <p class="feedback-preview">{{ fb.content }}</p>
            </div>
            <div v-if="adminFeedback.length === 0" class="no-members">Noch kein Feedback vorhanden.</div>
          </div>
        </div>

        <!-- Modal: Group Members Details -->
        <Teleport to="body">
          <div v-if="selectedAdminPool" class="admin-modal-overlay" @click="selectedAdminPool = null">
            <div class="admin-modal-card" @click.stop>
              <div class="modal-header">
                <h3>Mitglieder: {{ selectedAdminPool.name }}</h3>
                <button class="close-btn" @click="selectedAdminPool = null">
                  <img src="@/assets/images/cross.svg" alt="Schließen">
                </button>
              </div>
              <div class="modal-body">
                <LoadingSpinner v-if="membersLoading" message="Mitglieder werden geladen..."/>
                <div v-else-if="poolMembers.length === 0" class="no-members">
                  Keine Mitglieder in dieser Gruppe.
                </div>
                <div v-else class="members-list">
                  <div v-for="member in poolMembers" :key="member.user_id" class="member-card">
                    <div class="member-header">
                      <div class="member-user-info">
                        <span class="member-email">{{ member.email }}</span>
                        <span class="role-badge" :class="{ admin: member.is_pool_admin }">
                          {{ member.is_pool_admin ? 'Gruppen-Admin' : 'Mitglied' }}
                        </span>
                        <span v-if="member.is_deleted" class="status-badge left">Ausgetreten</span>
                      </div>
                    </div>

                    <!-- Profile Details (if user has profile in this pool) -->
                    <div v-if="member.profile_id" class="member-profile-details">
                      <div class="profile-main-info">
                        <strong>{{ member.name }}</strong> ({{ calculateAge(member.birthday) }} Jahre, {{ member.height }} cm)
                      </div>
                      <div v-if="member.looking_for" class="profile-looking">
                        <strong>Sucht:</strong> {{ member.looking_for }}
                      </div>
                      <div v-if="member.short_bio" class="profile-bio short">
                        <em>„{{ member.short_bio }}“</em>
                      </div>
                      <div v-if="member.long_bio" class="profile-bio long">
                        {{ member.long_bio }}
                      </div>
                      <div v-if="member.availability && parseAvailability(member.availability).length > 0" class="profile-availability">
                        <strong>Verfügbarkeit:</strong>
                        <span class="availability-badge" v-for="day in parseAvailability(member.availability)" :key="day.name">
                          {{ day.name }}
                        </span>
                      </div>
                    </div>
                    <div v-else class="no-profile-badge">
                      Kein Profil in dieser Gruppe erstellt.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Teleport>

        <!-- Modal: Feedback Details -->
        <Teleport to="body">
          <div v-if="selectedFeedback" class="admin-modal-overlay" @click="selectedFeedback = null">
            <div class="admin-modal-card" @click.stop>
              <div class="modal-header">
                <h3>Feedback-Detail</h3>
                <button class="close-btn" @click="selectedFeedback = null">
                  <img src="@/assets/images/cross.svg" alt="Schließen">
                </button>
              </div>
              <div class="modal-body">
                <div class="feedback-detail-meta">
                  <span class="feedback-email">{{ selectedFeedback.user_email || 'Anonym' }}</span>
                  <span class="feedback-date">{{ formatDate(selectedFeedback.created_at) }}</span>
                </div>
                <p class="feedback-full-content">{{ selectedFeedback.content }}</p>
              </div>
            </div>
          </div>
        </Teleport>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService.js';
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Multiselect from 'vue-multiselect';
import GroupSelector from "@/components/GroupSelector.vue";
import moment from 'moment';
export default {
  "name": "AccountView",
  data() {
    return {
      user: null,
      isAdmin: false,
      accountEmail: null,
      pools: null,
      pools_available: false,
      selectedPool: null,
      details_expanded: false,
      noProfileYet: false,
      profileLoading: false,
      uploadingImage: false,
      submitting: false,
      creatingProfile: false,
      availability_options : [
        {name: 'Mo', code: 'mo'},
        {name: 'Di', code: 'di'},
        {name: 'Mi', code: 'mi'},
        {name: 'Do', code: 'do'},
        {name: 'Fr', code: 'fr'},
        {name: 'Sa', code: 'sa'},
        {name: 'So', code: 'so'}
      ],
      profile: {
        name: null,
        gender: null,
        birthday: null,
        height: null,
        looking_for: null,
        availability: [],
        short_bio: null,
        long_bio: null,
        images: []
      },
      gender_options: [
        {value: 'male', label: 'Männlich'},
        {value: 'female', label: 'Weiblich'},
        {value: 'diverse', label: 'Divers'}
      ],
      errors: [],
      errorToastVisible: false,
      errorToastTimeout: null,
      fileToUpload: null,
      activeTab: 'profile',
      latestUsers: [],
      adminPools: [],
      selectedAdminPool: null,
      poolMembers: [],
      membersLoading: false,
      adminFeedback: [],
      selectedFeedback: null,
      homepageHitsCount: null
    }
  },
  components: {
    LoadingSpinner,
    Multiselect,
    GroupSelector
  },
  methods: {
    logout() {
      this.$store.dispatch('logout');
      this.$router.push('/login');
    },
    poolChanged() {
      this.profile = null
      this.noProfileYet = false
      this.getProfile(this.selectedPool.id)
      try {
        AuthService.updateSelectedPool({selected_pool: this.selectedPool.id})
        this.$store.dispatch('updateLastSelectedPool', {pool_id: this.selectedPool.id})
      } catch(e) {
        console.error(e)
      }
    },
    async getProfilePrefill() {
      let result = await AuthService.getProfilePrefill()
      if(result.success && result.msg.length > 0) {
        this.profile.name = result.msg[0].name
        this.profile.height = result.msg[0].height
        this.profile.birthday = moment(result.msg[0].birthday).format("YYYY-MM-DD")
      }
    },
    async onFileChange(file) {
      this.fileToUpload = file

      const formData = new FormData();
      formData.append('image', this.fileToUpload);
      formData.append('pool_id', this.selectedPool.id);

      try {
        this.uploadingImage = true
        let result = await AuthService.uploadImage(formData, {headers: {'Content-Type': 'multipart/form-data'}})
        this.uploadingImage = false
        if(result.success) {
          if(result.profile_id) {
            this.profile.profile_id = result.profile_id
          }
          this.profile.images = await this.buildImageArray(this.profile.profile_id)
        }
      } catch (e) {
        console.error(e)
      }
    },
    async removeImage(image_id){
      let response = await AuthService.removeImage({image_id: image_id})
      if(response.success) {
        this.profile.images = this.profile.images.filter(i => i.image_id !== image_id)
      } else {
        console.error(response.msg)
      }
    },
    showErrorToast() {
      clearTimeout(this.errorToastTimeout)
      this.errorToastVisible = true
      this.errorToastTimeout = setTimeout(() => {
        this.errorToastVisible = false
      }, 3000)
    },
    async submitProfile() {
      if(!this.validator()) {
        this.showErrorToast()
        return 0
      }

      this.submitting = true

      let data = {
        ...this.profile,
        availability: JSON.stringify(this.profile.availability),
        pool_id: this.selectedPool.id
      }

      let msg = await AuthService.submitUserProfile(data)
      if(msg.success) {
        this.$router.go()
      }
      this.submitting = false
      
    },
    async getUserJoinedPools() {
      try {
        this.msg = await AuthService.getUserJoinedPools()
        if (this.msg.success && this.msg.msg.length > 0) {
          this.pools = this.msg.msg
          if(this.pools.length > 0) this.pools_available = true
          this.selectedPool = this.msg.msg.find(res => res.id === this.$route.query.pool)
            || this.msg.msg.find(res => res.id === this.user.last_selected_pool)
            || this.msg.msg[0]
          await this.getProfile(this.selectedPool.id)
        }
      } catch (e) {
        console.error(e)
        this.errorMsg = e.response.data.msg
      }
    },
    async getProfile(pool_id) {
      if(!pool_id) return

      this.profileLoading = true

      try {
        this.msg = await AuthService.getUserProfileFromPool({pool_id: pool_id})
        if (this.msg.success) {
          if(this.msg.msg.length > 0) {
            this.profile = {...this.msg.msg[0], "age": Math.floor(moment().diff(moment(this.msg.msg[0].birthday), 'days') / 365),
              "birthday": moment(this.msg.msg[0].birthday).format("YYYY-MM-DD"),
              "availability": this.msg.msg[0].availability ? JSON.parse(this.msg.msg[0].availability) : [],
              "images": await this.buildImageArray(this.msg.msg[0].profile_id)
            }
            console.log(this.profile)
          } else {
            this.noProfileYet = true
          }
        }
      } catch (e) {
        console.error(e)
        this.errorMsg = e.response.data.msg
      }

      this.profileLoading = false
    },
    async buildImageArray(profile_id) {
      const result = await AuthService.getProfileImages({ profile_id });

      if (!result.success) return [];

      const imagePromises = result.msg.map(async image_id => {
        try {
          const img = await AuthService.getImage({ image_id: image_id.image_id });
          return {image_id: image_id.image_id, image: URL.createObjectURL(img)};
        } catch (error) {
          if (error.response && error.response.status === 404) {
            console.warn(`Bild mit ID ${image_id.image_id} nicht gefunden (404).`);
            return null; // Optional: Fallback-Bild-URL hier zurückgeben
          } else {
            console.error(`Fehler beim Laden des Bildes ${image_id.image_id}:`, error);
            return null;
          }
        }
      });

      let response = await Promise.all(imagePromises);
      return response
    },
    openUploadDialog(){
      let input = document.createElement('input');
      input.type = 'file';
      input.onchange = () => {
        let files = Array.from(input.files);
        this.onFileChange(files[0])
      };
      input.click();
    },
    addTag (newTag) {
      const tag = {
        name: newTag,
        code: newTag.substring(0, 2) + Math.floor((Math.random() * 10000000))
      }
      this.availability_options.push(tag)
      this.profile.availability.push(tag)
    },
    createProfileFromRedirect(){
      if(this.$route.query.create && this.noProfileYet && !this.creatingProfile && this.pools_available) {
        this.creatingProfile = true
        this.getProfilePrefill()
        this.details_expanded = true;
      }
    },
    validator() {
      this.errors = []
      let missingRequired = false
      let required_fields = [{slug: "name", t: "Name"}, {slug: "birthday", t: "Geburtstag"}, {slug: "height", t: "Körpergröße"}, {slug: "looking_for", t: "Wonach suchst du?"}]

      required_fields.forEach(required => {
        if(!this.profile[required.slug]) {
          document.getElementById(required.slug).classList.add("form-error")
          missingRequired = true
        } else {
          document.getElementById(required.slug).classList.remove("form-error")
        }
      })

      if(this.profile.birthday && moment().diff(moment(this.profile.birthday), 'years') < 12) {
        document.getElementById("birthday").classList.add("form-error")
        this.errors.push({field: "birthday", e: "Du musst mindestens 12 Jahre alt sein!"})
      }

      if(this.profile.height && this.profile.height < 100) {
        document.getElementById("height").classList.add("form-error")
        this.errors.push({field: "height", e: "Die Mindestgröße beträgt 100cm"})
      }

      if(this.profile.name && this.profile.name.length < 3) {
        document.getElementById("name").classList.add("form-error")
        this.errors.push({field: "name", e: "Dein Name muss mindestens drei Zeichen lang sein!"})
      }

      if(this.profile.looking_for && this.profile.looking_for.length < 5) {
        document.getElementById("looking_for").classList.add("form-error")
        this.errors.push({field: "looking_for", e: "Gib mindestens 5 Zeichen ein."})
      }

      console.log(this.errors)
      return this.errors.length === 0 && !missingRequired
    },
    async loadAdminData() {
      try {
        const usersRes = await AuthService.getAdminLatestUsers();
        if (usersRes.success) {
          this.latestUsers = usersRes.msg;
        }
        const poolsRes = await AuthService.getAdminPools();
        if (poolsRes.success) {
          this.adminPools = poolsRes.msg;
        }
        const feedbackRes = await AuthService.getAdminFeedback();
        if (feedbackRes.success) {
          this.adminFeedback = feedbackRes.msg;
        }
        const hitsRes = await AuthService.getAdminHomepageHitsCount();
        if (hitsRes.success) {
          this.homepageHitsCount = hitsRes.msg;
        }
      } catch (e) {
        console.error(e);
      }
    },
    selectFeedback(fb) {
      this.selectedFeedback = fb;
    },
    async selectAdminPool(pool) {
      this.selectedAdminPool = pool;
      this.poolMembers = [];
      this.membersLoading = true;
      try {
        const res = await AuthService.getAdminPoolMembers(pool.id);
        if (res.success) {
          this.poolMembers = res.msg;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.membersLoading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD.MM.YYYY HH:mm');
    },
    calculateAge(birthday) {
      if (!birthday) return '-';
      return moment().diff(moment(birthday), 'years');
    },
    parseAvailability(avail) {
      if (!avail) return [];
      try {
        return typeof avail === 'string' ? JSON.parse(avail) : avail;
      } catch (e) {
        return [];
      }
    }
  },
  async beforeCreate() {
    if (!this.$store.getters.isLoggedIn) {
      this.$router.push('/login');
    }
  },
  async mounted() {
    try {
      this.msg = await AuthService.getUser()
      this.isAdmin = !!this.msg.msg[0].isAdmin;
      this.accountEmail = this.msg.msg[0].email
      this.user = this.msg.msg[0]
    } catch(e){
      console.error(e)
    }

    await this.getUserJoinedPools()
    this.createProfileFromRedirect()
  }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.multiselect__tag, .multiselect__option--highlight {
  background: var(--primary-color) !important;
}
</style>
<style scoped>
.main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding-top: 20px;
  background: transparent;
}

h1 {
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 24px 0;
  color: var(--text-on-gradient);
}

.error-toast {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #FF3B30;
  color: white;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: var(--radius-m);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  text-align: center;
}

.error-toast-fade-enter-active,
.error-toast-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.error-toast-fade-enter-from,
.error-toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

.joined-banner {
  width: 100%;
  background: #E8F5E9;
  color: #2E7D32;
  font-weight: 700;
  padding: 14px 18px;
  border-radius: var(--radius-m);
  margin-bottom: 16px;
  text-align: center;
  box-sizing: border-box;
}

/* Header & Pool Selector */
.header-controls {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-bottom: 24px;
}

.settings-link {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-color);
  width: 44px;
  height: 44px;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-soft);
  box-sizing: border-box;
  transition: all 0.2s ease;
  flex-shrink: 0;
  border: none;
}

.settings-link:active {
  transform: scale(0.95);
}

.settings-link img {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
}

/* Profile Content Wrapper */
.profile-wrapper {
  width: 100%;
  max-width: var(--max-width);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px 40px;
  box-sizing: border-box;
}

/* Profile Card */
.match-selection-container {
  width: 100%;
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-strong);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.match-selection-container.details_expanded {
  padding-bottom: 40px;
}

.match-info {
  padding: 32px;
  text-align: left;
}

.match-info h2 {
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0 0 20px 0;
}

.match-stat {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: var(--text-secondary);
}

.match-stat p {
  margin: 0;
}

.match-stat img {
  width: 20px;
  opacity: 0.5;
}

.match-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.match-dates p {
  background: #F5F5F7;
  padding: 4px 12px;
  border-radius: var(--radius-s);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-main);
}

.match-note {
  background: #F5F5F7;
  padding: 16px;
  border-radius: var(--radius-m);
  margin-top: 12px;
}

.details-container {
  margin: 12px 0 24px;
  font-size: 1rem;
  line-height: 1.5;
}

/* Edit Form */
.match-info.edit {
  padding: 16px;
}

.match-info.edit .match-stat {
  margin-bottom: 8px;
}

.match-info.edit h3 {
  margin: 12px 0 6px;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-main);
}

.match-info.edit input,
.match-info.edit textarea,
.match-info.edit select {
  width: 100%;
  background: #F5F5F7;
  border: 1px solid transparent;
  padding: 10px 14px;
  border-radius: var(--radius-m);
  font-family: var(--font);
  font-size: 0.95rem;
  margin-bottom: 2px;
  outline: none;
  box-sizing: border-box;
}

.match-info.edit input:focus,
.match-info.edit textarea:focus,
.match-info.edit select:focus {
  background: white;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(56, 163, 165, 0.15);
}

.match-info.edit input.form-error,
.match-info.edit textarea.form-error,
.match-info.edit select.form-error {
  border-color: #FF3B30;
}

#name {
  font-size: 1.4rem;
  font-weight: 800;
  margin-bottom: 12px;
}

.profile-error {
  color: #FF3B30;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 8px;
  margin-left: 4px;
}

.details-container.edit {
  margin: 6px 0 10px;
}

/* Photo Management */
.photos-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 10px 0;
  -webkit-overflow-scrolling: touch;
}

.single-photo {
  position: relative;
  flex-shrink: 0;
}

.single-photo img {
  width: 110px;
  height: 145px;
  object-fit: cover;
  border-radius: var(--radius-m);
}

.single-photo .delete {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  box-shadow: var(--shadow-soft);
}

.single-photo .delete img {
  width: 100%;
  height: 100%;
}

.single-photo button:not(.delete) {
  width: 110px;
  height: 110px;
  background: #F5F5F7;
  border: 2px dashed var(--border-color);
  color: var(--text-secondary);
  border-radius: var(--radius-m);
  font-size: 0.8rem;
}

/* Buttons */
.edit-btn {
  width: 100%;
  background: var(--primary-color);
  color: white;
  padding: 12px;
  border-radius: var(--radius-m);
  font-weight: 700;
  margin-top: 8px;
}

.submit-buttons {
  padding: 0 16px 16px;
  display: flex;
  gap: 12px;
}

.edit-btn.logout {
  background: #F5F5F7;
  color: #FF3B30;
}

.add_profile_wrapper {
  text-align: center;
  margin-top: 60px;
}

.add_profile {
  background: var(--primary-color);
  color: white;
  padding: 16px 32px;
  border-radius: var(--radius-xl);
  font-weight: 700;
}

/* Tab Selector */
.tab-selector {
  display: flex;
  background: rgba(0, 0, 0, 0.05);
  padding: 4px;
  border-radius: var(--radius-xl);
  margin-bottom: 24px;
  width: 100%;
  box-sizing: border-box;
}

.tab-selector button {
  flex: 1;
  padding: 10px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: var(--radius-l);
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-selector button.active {
  background: white;
  color: var(--text-main);
  box-shadow: var(--shadow-soft);
}

/* Admin Panel */
.admin-panel h2 {
  font-size: 1.8rem;
  font-weight: 800;
  color: var(--text-on-gradient);
  margin: 0;
  text-align: left;
}

.admin-panel .admin-section:last-child {
  margin-bottom: 50px;
}

.admin-section {
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: var(--radius-xl);
  padding: 24px;
  box-shadow: var(--shadow-strong);
  text-align: left;
  border: 1px solid var(--border-color);
  box-sizing: border-box;
}

.admin-section h3 {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1D1D1F;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
}

.section-desc {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: -8px 0 16px 0;
}

.stat-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--primary-color);
  letter-spacing: -0.02em;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 600;
}

.admin-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
}

.admin-list::-webkit-scrollbar {
  width: 6px;
}
.admin-list::-webkit-scrollbar-track {
  background: transparent;
}
.admin-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.admin-item {
  background: #F5F5F7;
  border: 1px solid transparent;
  border-radius: var(--radius-m);
  padding: 16px;
  transition: all 0.2s ease;
}

.admin-item.clickable {
  cursor: pointer;
}

.admin-item.clickable:hover {
  background: white;
  border-color: var(--primary-color);
  box-shadow: var(--shadow-soft);
}

.admin-item.clickable.selected {
  background: rgba(56, 163, 165, 0.08);
  border-color: var(--primary-color);
}

/* User Items */
.user-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-email {
  font-weight: 700;
  color: #1D1D1F;
  font-size: 0.95rem;
}

.user-dates {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* Pool Items */
.pool-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.pool-header h4 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1D1D1F;
}

.pool-code {
  color: var(--primary-color);
  font-weight: 600;
}

.status-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 12px;
  background: #E8F5E9;
  color: #2E7D32;
}

.status-badge.archived {
  background: #FFE0B2;
  color: #E65100;
}

.pool-desc {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.pool-date {
  font-size: 0.75rem;
  color: var(--text-secondary);
  opacity: 0.8;
}

/* Members Section */
.members-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.member-card {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-m);
  padding: 18px;
  background: #F5F5F7;
}

.member-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 10px;
}

.member-user-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.member-email {
  font-weight: 700;
  color: #1D1D1F;
  font-size: 0.95rem;
}

.role-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  background: #ECEFF1;
  color: #37474F;
}

.role-badge.admin {
  background: #E3F2FD;
  color: #1565C0;
}

.status-badge.left {
  background: #FFEBEE;
  color: #C62828;
}

.member-profile-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #1D1D1F;
}

.profile-main-info {
  font-size: 0.95rem;
}

.profile-looking {
  color: var(--text-secondary);
}

.profile-bio.short {
  background: white;
  padding: 8px 12px;
  border-radius: var(--radius-s);
  border-left: 3px solid var(--primary-color);
  font-size: 0.85rem;
}

.profile-bio.long {
  white-space: pre-line;
  color: #333333;
}

.profile-availability {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.availability-badge {
  background: white;
  padding: 2px 8px;
  border-radius: var(--radius-s);
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid var(--border-color);
}

.no-members {
  font-size: 0.95rem;
  color: var(--text-secondary);
  text-align: center;
  padding: 20px 0;
}

/* Modal Styling */
.admin-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.admin-modal-card {
  background: var(--card-bg, rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.25));
  width: 90%;
  max-width: 550px;
  max-height: 80vh;
  border-radius: var(--radius-xl, 24px);
  box-shadow: var(--shadow-strong, 0 20px 40px rgba(0, 0, 0, 0.2));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.admin-modal-card .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.admin-modal-card .modal-header h3 {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  color: #1D1D1F;
  letter-spacing: -0.02em;
}

.admin-modal-card .close-btn {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, transform 0.2s ease;
  padding: 0;
}

.admin-modal-card .close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: scale(1.05);
}

.admin-modal-card .close-btn:active {
  transform: scale(0.95);
}

.admin-modal-card .close-btn img {
  width: 14px;
  height: 14px;
  opacity: 0.6;
}

.admin-modal-card .modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  box-sizing: border-box;
}

.admin-modal-card .modal-body::-webkit-scrollbar {
  width: 6px;
}
.admin-modal-card .modal-body::-webkit-scrollbar-track {
  background: transparent;
}
.admin-modal-card .modal-body::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.no-profile-badge {
  font-size: 0.85rem;
  color: var(--text-secondary);
  background: rgba(0, 0, 0, 0.02);
  padding: 10px;
  border-radius: var(--radius-s);
  border: 1px dashed var(--border-color);
  text-align: center;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Feedback List */
.feedback-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feedback-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.feedback-email {
  font-weight: 700;
  color: #1D1D1F;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.feedback-date {
  font-size: 0.75rem;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
}

.feedback-preview {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

/* Feedback Modal Body */
.feedback-detail-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.feedback-full-content {
  margin: 0;
  font-size: 1rem;
  color: #1D1D1F;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>

